<script setup lang="ts">
/**
 * Choosing a picture, with the picture shown.
 *
 * A bare file input tells somebody nothing: not whether an image is already
 * there, not which one, and not what it looks like at the size it will print.
 * So this is a placeholder that becomes the image once there is one, takes a
 * drop as well as a click, and offers to remove what it holds.
 *
 * The file is read into the document as a data URL rather than uploaded and
 * linked. That makes the document larger and makes it self-contained, which is
 * the trade worth taking: a report opened next year should not depend on a
 * file server somebody has since tidied up.
 */
import { useI18n } from 'vue-i18n'

import { computed, ref } from 'vue'

const { t } = useI18n()

/**
 * Beyond this, a document becomes slow to save and slow to open.
 *
 * A cover photograph at 300dpi across A4 is comfortably inside it; a phone
 * photograph straight off the camera is not, which is the case this catches.
 */
const MAX_BYTES = 4 * 1024 * 1024

const props = withDefaults(
  defineProps<{
    /** The image as a data URL, or empty. */
    source: string
    label: string
    hint?: string
    /** How the placeholder is drawn: a wide band, or a small mark. */
    shape?: 'wide' | 'mark'
  }>(),
  { hint: '', shape: 'wide' },
)

const emit = defineEmits<{
  change: [source: string]
}>()

const problem = ref<string | null>(null)
const over = ref(false)

const has = computed(() => props.source !== '')

function take(file: File | undefined): void {
  if (!file) return

  problem.value = null

  if (!file.type.startsWith('image/')) {
    problem.value = t('inspector.image.notAnImage')
    return
  }

  if (file.size > MAX_BYTES) {
    problem.value = t('inspector.image.tooLarge')
    return
  }

  const reader = new FileReader()
  reader.onload = () => {
    if (typeof reader.result === 'string') emit('change', reader.result)
  }
  reader.onerror = () => {
    problem.value = t('inspector.image.unreadable')
  }
  reader.readAsDataURL(file)
}

function onChoose(event: Event): void {
  const input = event.target as HTMLInputElement
  take(input.files?.[0])
  // Cleared so choosing the same file twice in a row still fires a change.
  input.value = ''
}

function onDrop(event: DragEvent): void {
  over.value = false
  take(event.dataTransfer?.files?.[0])
}

function clear(): void {
  problem.value = null
  emit('change', '')
}
</script>

<template>
  <div class="picker">
    <div class="picker__head">
      <span class="picker__label">{{ props.label }}</span>
      <button v-if="has" type="button" class="picker__clear" @click="clear">
        {{ t('inspector.image.remove') }}
      </button>
    </div>

    <label
      class="well"
      :class="{ 'well--over': over, 'well--has': has, [`well--${props.shape}`]: true }"
      @dragover.prevent="over = true"
      @dragenter.prevent="over = true"
      @dragleave="over = false"
      @drop.prevent="onDrop"
    >
      <input type="file" accept="image/*" class="well__input" @change="onChoose" />

      <img v-if="has" class="well__image" :src="props.source" alt="" />

      <template v-else>
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
          <rect
            x="3"
            y="4.5"
            width="18"
            height="15"
            rx="2.5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
          />
          <circle cx="8.6" cy="10" r="1.7" fill="none" stroke="currentColor" stroke-width="1.4" />
          <path
            d="M3.4 17.2 8.8 12l3.4 3.2 3.2-2.8 5.2 4.6"
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span class="well__prompt">{{ t('inspector.image.choose') }}</span>
      </template>

      <!-- Over the image once there is one, so replacing it is the same
           gesture as choosing it was. -->
      <span v-if="has" class="well__replace">{{ t('inspector.image.replace') }}</span>
    </label>

    <p v-if="props.hint && !problem" class="picker__hint">{{ props.hint }}</p>
    <p v-if="problem" class="picker__problem" role="alert">{{ problem }}</p>
  </div>
</template>

<style scoped>
.picker {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  min-width: 0;
}

.picker__head {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
}

.picker__label {
  color: var(--ink-faint);
  font-size: var(--text-xs);
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.picker__clear {
  margin-left: auto;
  padding: 0;
  border: 0;
  background: none;
  color: var(--ink-faint);
  font: inherit;
  font-size: var(--text-xs);
  cursor: pointer;
  transition: color var(--quick);
}

.picker__clear:hover {
  color: var(--danger);
}

.well {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp-3);
  overflow: hidden;
  border-radius: var(--radius);
  background: var(--panel-raised);
  outline: 1.5px dashed var(--hairline-strong);
  outline-offset: -1.5px;
  color: var(--ink-faint);
  cursor: pointer;
  transition:
    outline-color var(--quick),
    background var(--quick),
    color var(--quick);
}

/* Roughly the proportion of the space a cover gives a picture, so what is
   previewed here is close to what lands on the page. */
.well--wide {
  aspect-ratio: 16 / 9;
}

.well--mark {
  aspect-ratio: 5 / 2;
}

.well:hover,
.well:focus-within {
  outline-color: var(--accent-line);
  color: var(--ink-soft);
}

.well--over {
  outline-style: solid;
  outline-color: var(--accent);
  background: var(--accent-quiet);
  color: var(--accent);
}

/* Once it holds an image the dashes are noise: the image is its own edge. */
.well--has {
  outline-style: solid;
  outline-color: var(--hairline);
  /* A chequer, so a logo on a transparent ground is visibly transparent
     rather than looking as though it has a white box around it. */
  background-color: var(--panel-raised);
  background-image:
    linear-gradient(45deg, var(--hairline) 25%, transparent 25%),
    linear-gradient(-45deg, var(--hairline) 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, var(--hairline) 75%),
    linear-gradient(-45deg, transparent 75%, var(--hairline) 75%);
  background-size: 12px 12px;
  background-position:
    0 0,
    0 6px,
    6px -6px,
    -6px 0;
}

.well__input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.well__image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.well__prompt {
  font-size: var(--text-sm);
}

.well__replace {
  position: absolute;
  inset: auto 0 0 0;
  padding: var(--sp-2) var(--sp-4);
  background: rgba(3, 12, 24, 0.68);
  color: #ffffff;
  font-size: var(--text-xs);
  text-align: center;
  opacity: 0;
  transition: opacity var(--quick);
}

.well--has:hover .well__replace,
.well--has:focus-within .well__replace {
  opacity: 1;
}

.picker__hint {
  margin: 0;
  color: var(--ink-faint);
  font-size: var(--text-xs);
  line-height: 1.45;
}

.picker__problem {
  margin: 0;
  color: var(--danger);
  font-size: var(--text-xs);
}
</style>
